import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  Receipt,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { reportApi } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { useTranslation } from 'react-i18next';
import { ReportDateRangeFilter } from '../components/reports/ReportDateRangeFilter';
import { ReportPieChart } from '../components/reports/ReportPieChart';
import { useReportDateRange } from '../hooks/useReportDateRange';
import { getQueryErrorMessage } from '../utils/queryError';

export const FinancialReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const { range, setRange, minimumStartDate } = useReportDateRange();

  const { data: report, error, isError, isLoading } = useQuery(
    ['totalProfitReport', range.startDate, range.endDate],
    () => reportApi.getTotalProfit(range.startDate, range.endDate)
  );

  const revenue = report?.totalRevenue ?? 0;
  const totalExpenses = report?.totalExpenses ?? 0;
  const profit = report?.totalProfit ?? 0;
  const margin = report?.profitMarginPercent ?? 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('financial.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('financial.subtitle')}
      </Typography>

      <ReportDateRangeFilter
        value={range}
        onChange={setRange}
        minimumStartDate={minimumStartDate}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          {getQueryErrorMessage(
            error,
            t('financial.loadError', 'Unable to load the total profit report for that date range.')
          )}
        </Alert>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingUp color="success" />
                    <Typography variant="body2" color="text.secondary">
                      {t('financial.revenue')}
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="success.main">
                    {formatCurrency(revenue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('financial.revenueSubtitle')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Receipt color="error" />
                    <Typography variant="body2" color="text.secondary">
                      {t('financial.totalExpenses')}
                    </Typography>
                  </Box>
                  <Typography variant="h4" color="error.main">
                    {formatCurrency(totalExpenses)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('financial.totalExpensesSubtitle')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AttachMoney color="primary" />
                    <Typography variant="body2" color="text.secondary">
                      {t('financial.netProfit')}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    color={profit >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(profit)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('financial.netProfitSubtitle')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TrendingDown color="warning" />
                    <Typography variant="body2" color="text.secondary">
                      {t('financial.profitMargin')}
                    </Typography>
                  </Box>
                  <Typography variant="h4">{margin.toFixed(1)}%</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('financial.profitMarginSubtitle')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Trend + Expense Breakdown */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('financial.trend', 'Revenue vs. Expenses by Month')}
                  </Typography>
                  {!report || report.trend.length === 0 ? (
                    <Alert severity="info">{t('financial.empty')}</Alert>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('fields.date')}</TableCell>
                            <TableCell align="right">{t('financial.revenue')}</TableCell>
                            <TableCell align="right">{t('financial.totalExpenses')}</TableCell>
                            <TableCell align="right">{t('financial.netProfit')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {report.trend.map((point) => (
                            <TableRow key={`${point.year}-${point.month}`}>
                              <TableCell>{point.label}</TableCell>
                              <TableCell align="right">{formatCurrency(point.revenue)}</TableCell>
                              <TableCell align="right">{formatCurrency(point.expenses)}</TableCell>
                              <TableCell
                                align="right"
                                sx={{ color: point.profit >= 0 ? 'success.main' : 'error.main' }}
                              >
                                {formatCurrency(point.profit)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('financial.expenseBreakdown', 'Expense Breakdown by Category')}
                  </Typography>
                  <ReportPieChart
                    data={(report?.expenseByCategory ?? []).map((slice) => ({ name: slice.label, value: slice.amount }))}
                    emptyLabel={t('financial.empty', 'No data recorded for this period')}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};
